import { styled } from 'goober';
import { forwardRef } from 'react';
import { AppColorsType, ThemeColorKeys } from '../../base/useTheme';

type ButtonSize = keyof Omit<typeof SIZE_DEFAULTS, 'icon'>;

export type ButtonContent = 'icon' | 'icon-title' | 'title';

export type ButtonProps = {
    $size?: ButtonSize;
    $color?: ThemeColorKeys;
    $active?: boolean;
    $type: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'inverted' | 'plain';
};

function getButtonTypeDefaults(themeColorKey: ThemeColorKeys, themeColors: AppColorsType) {
    const color = themeColors[themeColorKey];
    return {
        primary: {
            border: 'none',
            color: themeColors.ui.white,
            fill: themeColors.ui.white,
            backgroundColor: color.w500,
            fontWeight: 500,
            '&:hover': {
                backgroundColor: color.w700,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                backgroundColor: themeColors.neutral.w200,
                color: themeColors.neutral.w700,
                fill: themeColors.neutral.w700,
            },
        },
        secondary: {
            border: 'none',
            color: color.w500,
            fill: color.w500,
            backgroundColor: color.w50,
            fontWeight: 500,
            '&:hover': {
                color: color.w500,
                fill: color.w500,
                backgroundColor: color.w100,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                fill: themeColors.neutral.w700,
                color: themeColors.neutral.w700,
                backgroundColor: themeColors.neutral.w100,
            },
        },
        quaternary: {
            border: 'none',
            color: themeColorKey === 'neutral' ? themeColors.ui.dark : color.w500,
            fill: themeColorKey === 'neutral' ? themeColors.ui.dark : color.w500,
            fontWeight: 400,
            backgroundColor: 'transparent',
            '&:hover': {
                color: themeColorKey === 'neutral' ? themeColors.ui.dark : color.w500,
                fill: themeColorKey === 'neutral' ? themeColors.ui.dark : color.w500,
                backgroundColor: themeColorKey === 'neutral' ? themeColors.neutral.w100 : color.w50,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                color: themeColors.neutral.w700,
                fill: themeColors.neutral.w700,
                backgroundColor: themeColors.neutral.w200,
            },
        },
        inverted: {
            border: 'none',
            color: themeColors.ui.white,
            fill: themeColors.ui.white,
            fontWeight: 400,
            backgroundColor: 'transparent',
            '&:hover': {
                color: themeColors.ui.white,
                fill: themeColors.ui.white,
                backgroundColor: themeColors.neutral.w800,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                color: themeColors.neutral.w400,
                fill: themeColors.neutral.w400,
            },
        },
        tertiary: {
            border: 'none',
            // use box shadow instead of border so button stays the same size
            boxShadow: `0 0 0 1px ${themeColors.neutral.w200} inset`,
            color: themeColors.neutral.w900,
            fontWeight: 400,
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: themeColors.neutral.w100,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                color: themeColors.neutral.w700,
                fill: themeColors.neutral.w700,
                backgroundColor: 'transparent',
            },
        },
        plain: {
            border: 'none',
            color: themeColors.ui.dark,
            fill: themeColors.ui.dark,
            fontWeight: 400,
            backgroundColor: themeColors.neutral.w100,
            '&:hover': {
                backgroundColor: themeColors.neutral.w200,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                color: themeColors.neutral.w400,
                fill: themeColors.neutral.w400,
            },
        },
    };
}

function getButtonTypeActive(themeColorKey: ThemeColorKeys, themeColors: AppColorsType) {
    const color = themeColors[themeColorKey];

    const text = themeColorKey === 'neutral' ? themeColors.ui.dark : color.w500;
    const background = themeColorKey === 'neutral' ? themeColors.neutral.w100 : color.w50;
    const hover = themeColorKey === 'neutral' ? themeColors.neutral.w300 : color.w100;
    const border = themeColorKey === 'neutral' ? themeColors.neutral.w200 : color.w100;

    return {
        // unused
        primary: {},
        secondary: {},
        inverted: {},

        // secondary toggle
        quaternary: {
            border: 'none',
            color: text,
            fill: text,
            backgroundColor: background,
            fontWeight: 400,
            '&:hover': {
                color: text,
                fill: text,
                backgroundColor: hover,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                fill: themeColors.neutral.w700,
                color: themeColors.neutral.w700,
                backgroundColor: themeColors.neutral.w100,
            },
        },
        // primary toggle
        tertiary: {
            boxShadow: `0 0 0 1px ${border} inset`,
            color: text,
            fill: text,
            backgroundColor: background,
            fontWeight: 400,
            '&:hover': {
                color: text,
                fill: text,
                backgroundColor: hover,
            },
            '&:disabled, &:disabled > .fs-button_spinner': {
                fill: themeColors.neutral.w700,
                color: themeColors.neutral.w700,
                backgroundColor: themeColors.neutral.w100,
            },
        },
        // for toggle group
        plain: {
            border: 'none',
            color: themeColors.ui.dark,
            fill: themeColors.ui.dark,
            fontWeight: 400,
            backgroundColor: themeColors.ui.white,
            boxShadow: '0px 0px 4px 2px #00000010',
            '&:hover': {
                color: themeColors.ui.dark,
                fill: themeColors.ui.dark,
                backgroundColor: themeColors.ui.white,
            },
        },
    };
}

export const SIZE_DEFAULTS = Object.freeze({
    small: {
        height: '24px',
        minWidth: '24px',
        fontSize: '12px',
        padding: '6px 8px',
        lineHeight: '15px',
        borderRadius: '6px',
    },
    large: {
        height: '32px',
        minWidth: '32px',
        fontSize: '12px',
        padding: '9px 12px',
        lineHeight: '14px',
        borderRadius: '8px',
    },
    icon: {
        small: { height: '24px', width: '24px' },
        large: { height: '32px', width: '32px' },
    },
});

export const ButtonIconContainer = styled('span')(() => {
    return {
        display: 'inline-flex',
    };
});

export const ButtonPrimitive = styled('button', forwardRef)`
    position: relative;
    box-sizing: border-box;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
    border-radius: 8px;
    border-style: solid;
    appearance: none;
    outline: none;
    cursor: pointer;
    transition: background 0.1s ease-in-out, color 0.1s ease-in-out;
    border-width: 2px;
    font-family: inherit;
    overflow: hidden;
    flex-shrink: 0;

    &:focus-visible {
        outline-style: solid;
        outline-color: ${(props) => props.theme.colors.ui.dark};
        outline-width: 1px;
    }

    &:disabled,
    &:disabled > .fs-button_spinner {
        cursor: not-allowed;
    }

    ${ButtonIconContainer}, ${ButtonIconContainer} > svg,
    ${ButtonIconContainer} > .fs-icon_svg > .fs-icon_path,
    ${ButtonIconContainer} > .fs-icon_svg > .fs-icon_rect {
        color: inherit;
        fill: inherit;
        background-color: inherit;
    }
`;

export const Button = styled(
    ButtonPrimitive,
    forwardRef
)<ButtonProps & { $content: ButtonContent; $alignIcon: 'left' | 'right' }>(
    ({ theme, $color = 'primary', $type = 'primary', $size = 'large', $active, $content, $alignIcon }) => {
        const buttonTypeDefaults = getButtonTypeDefaults($color, theme.colors)[$type];
        const buttonTypeStyle = $active
            ? { ...buttonTypeDefaults, ...getButtonTypeActive($color, theme.colors)[$type] }
            : buttonTypeDefaults;
        const sizeDefault = $content === 'icon' ? SIZE_DEFAULTS.icon[$size] : SIZE_DEFAULTS[$size];
        const iconPadding = $content === 'icon-title' ? ($size === 'small' ? '4px' : '6px') : undefined;
        return {
            ...sizeDefault,
            ...buttonTypeStyle,
            [$alignIcon === 'left' ? 'paddingLeft' : 'paddingRight']: iconPadding,
            '& > span > svg:not(.fs-icon_svg)': {
                [$alignIcon === 'left' ? 'marginRight' : 'marginLeft']: $size === 'small' ? '8px' : '16px',
                [$alignIcon === 'left' ? 'marginLeft' : 'marginRight']:
                    $content === 'icon' ? ($size === 'small' ? '8px' : '16px') : undefined,
            },
            'span > .fs-icon_svg': {
                [$alignIcon === 'left' ? 'marginRight' : 'marginLeft']:
                    $content === 'icon'
                        ? $size === 'small'
                            ? '-6px'
                            : '-8px'
                        : $content === 'icon-title'
                        ? '0px'
                        : undefined,
                [$alignIcon === 'left' ? 'marginLeft' : 'marginRight']:
                    $content === 'icon' || $content === 'icon-title'
                        ? $size === 'small'
                            ? '-6px'
                            : '-8px'
                        : undefined,
                width: sizeDefault.height,
                height: sizeDefault.height,
            },
        };
    }
);
